import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useWeeklyStreak } from "@/hooks/useWeeklyStreak";

const VEG_CONFIG = [
  { type: "carrot", name: "Carrot", emoji: "🥕", scoreNeeded: 50, stages: ["🌱", "🌿", "🥕"], color: "bg-orange-50 border-orange-200", tagColor: "bg-orange-100 text-orange-700" },
  { type: "tomato", name: "Tomato", emoji: "🍅", scoreNeeded: 120, stages: ["🌱", "🌿", "🍅"], color: "bg-red-50 border-red-200", tagColor: "bg-red-100 text-red-700" },
  { type: "corn", name: "Corn", emoji: "🌽", scoreNeeded: 200, stages: ["🌱", "🌿", "🌽"], color: "bg-yellow-50 border-yellow-200", tagColor: "bg-yellow-100 text-yellow-700" },
  { type: "watermelon", name: "Watermelon", emoji: "🍉", scoreNeeded: 350, stages: ["🌱", "🌿", "🍉"], color: "bg-emerald-50 border-emerald-200", tagColor: "bg-emerald-100 text-emerald-700" },
  { type: "strawberry", name: "Strawberry", emoji: "🍓", scoreNeeded: 500, stages: ["🌱", "🌿", "🍓"], color: "bg-rose-50 border-rose-200", tagColor: "bg-rose-100 text-rose-700" },
  { type: "pumpkin", name: "Pumpkin", emoji: "🎃", scoreNeeded: 800, stages: ["🌱", "🌿", "🎃"], color: "bg-amber-50 border-amber-200", tagColor: "bg-amber-100 text-amber-700" },
];

const LEADERBOARD = [
  { name: "Emily W.", score: 1240, farm: ["🎃","🍉","🍅","🥕"], streak: 12 },
  { name: "Jason L.", score: 980, farm: ["🍉","🌽","🍅","🥕"], streak: 9 },
  { name: "Priya M.", score: 760, farm: ["🌽","🍅","🥕","🥕"], streak: 7 },
  { name: "Carlos R.", score: 540, farm: ["🍅","🥕","🥕"], streak: 5 },
  { name: "Yuki T.", score: 320, farm: ["🌽","🥕"], streak: 4 },
];

const DEMO_FARM = ["🥕","🥕","🍅","🥕","🌽","🍅","🍉","🎃","🥕","🍅","🌽","🥕","🍓","🍅","🥕","🌽","🌱","🌱","🌱","🌱"];

export default function FarmPage() {
  useWeeklyStreak();
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [farmPlots, setFarmPlots] = useState<string[]>(Array(20).fill("🌱"));
  const [plantingVeg, setPlantingVeg] = useState<string | null>(null);
  const [selectedPlot, setSelectedPlot] = useState<number | null>(null);
  const [toast, setToast] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState<"farm"|"shop"|"leaderboard">("farm");

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        setIsLoggedIn(true);
        supabase.from("profiles").select("score,weekly_streak").eq("id", data.user.id).maybeSingle().then(({ data: p }) => {
          if (p) { setScore(p.score || 0); setStreak(p.weekly_streak || 0); }
        });
        supabase.from("farm_vegetables").select("*").eq("user_id", data.user.id).then(({ data: vegs }) => {
          if (vegs && vegs.length > 0) {
            const plots = Array(20).fill("🌱");
            vegs.forEach((v, i) => {
              if (i < 20) {
                const cfg = VEG_CONFIG.find((c) => c.type === v.vegetable_type);
                if (cfg) plots[i] = cfg.stages[Math.min(v.growth_stage, 2)];
              }
            });
            setFarmPlots(plots);
          } else {
            setFarmPlots(DEMO_FARM);
          }
        });
      } else {
        setScore(480);
        setStreak(5);
        setFarmPlots(DEMO_FARM);
      }
    });
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const handlePlant = async (vegType: string, plotIdx: number) => {
    const cfg = VEG_CONFIG.find((c) => c.type === vegType);
    if (!cfg) return;
    if (score < cfg.scoreNeeded) { showToast(`Need ${cfg.scoreNeeded} pts to plant ${cfg.name}!`); return; }
    const newPlots = [...farmPlots];
    newPlots[plotIdx] = cfg.stages[0];
    setFarmPlots(newPlots);
    setScore((s) => s - cfg.scoreNeeded);
    setPlantingVeg(null);
    setSelectedPlot(null);
    showToast(`${cfg.emoji} ${cfg.name} planted! Keep studying to help it grow!`);
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await supabase.from("farm_vegetables").insert({ user_id: user.id, vegetable_type: vegType, growth_stage: 0, score_spent: cfg.scoreNeeded });
      await supabase.from("profiles").update({ score: score - cfg.scoreNeeded }).eq("id", user.id);
    }
  };

  const handlePlotClick = (idx: number) => {
    if (farmPlots[idx] === "🌱") { setSelectedPlot(idx); setActiveTab("shop"); }
    else showToast("This plot is already growing something!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/60 to-white">
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500">
              <i className="ri-graduation-cap-fill text-white"></i>
            </div>
            <span className="font-bold text-gray-900 text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>Crown SAT &amp; University</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 bg-emerald-50 px-3 py-1.5 rounded-full">
              <i className="ri-seedling-line text-emerald-600 text-sm"></i>
              <span className="text-emerald-700 font-bold text-sm">{score} pts</span>
            </div>
            <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1.5 rounded-full">
              <i className="ri-fire-line text-amber-500 text-sm"></i>
              <span className="text-amber-700 font-bold text-sm">{streak}wk</span>
            </div>
            {!isLoggedIn && <Link to="/auth" className="px-4 py-2 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap">Sign In</Link>}
          </div>
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-5 py-3 rounded-full text-sm font-medium animate-bounce">
          {toast}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold tracking-widest uppercase mb-3">Farm Reward System</span>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Your Virtual Farm 🌾</h1>
          <p className="text-gray-400 text-base">Study hard, earn points, grow vegetables. The more you learn, the more your farm blooms!</p>
        </div>

        {/* Score cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { icon: "ri-star-line", label: "Total Score", val: score, color: "text-amber-500", bg: "bg-amber-50" },
            { icon: "ri-fire-line", label: "Weekly Streak", val: `${streak} weeks`, color: "text-orange-500", bg: "bg-orange-50" },
            { icon: "ri-plant-line", label: "Plots Growing", val: farmPlots.filter(p => p !== "🌱").length, color: "text-emerald-600", bg: "bg-emerald-50" },
            { icon: "ri-trophy-line", label: "Rank", val: "#12", color: "text-rose-500", bg: "bg-rose-50" },
          ].map((s) => (
            <div key={s.label} className={`rounded-2xl p-5 ${s.bg} text-center`}>
              <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white mx-auto mb-2">
                <i className={`${s.icon} text-lg ${s.color}`}></i>
              </div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
              <p className="text-gray-500 text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex bg-stone-100 rounded-full p-1 mb-8 w-fit">
          {([["farm","🌾 My Farm"],["shop","🛒 Plant Shop"],["leaderboard","🏆 Leaderboard"]] as [typeof activeTab, string][]).map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>{label}</button>
          ))}
        </div>

        {/* Farm Grid */}
        {activeTab === "farm" && (
          <div>
            <p className="text-sm text-gray-400 mb-4">Click an empty plot (🌱) to plant a vegetable!</p>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-3 mb-6">
              {farmPlots.map((plot, i) => (
                <button key={i} onClick={() => handlePlotClick(i)} className={`aspect-square flex items-center justify-center rounded-xl text-2xl border-2 transition-all cursor-pointer ${plot === "🌱" ? "bg-emerald-50 border-emerald-100 hover:border-emerald-400 hover:scale-105" : "bg-white border-gray-100 hover:scale-105"}`}>
                  {plot}
                </button>
              ))}
            </div>
            <div className="bg-emerald-50 rounded-2xl p-5">
              <h4 className="font-semibold text-gray-800 text-sm mb-3">How to Earn More Points</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { a: "Weekly login", p: "+10 pts", i: "ri-calendar-check-line" },
                  { a: "Complete a lesson", p: "+20 pts", i: "ri-book-2-line" },
                  { a: "Attend live session", p: "+30 pts", i: "ri-video-line" },
                  { a: "Submit an essay", p: "+50 pts", i: "ri-file-text-line" },
                  { a: "Refer a friend", p: "+100 pts", i: "ri-user-add-line" },
                  { a: "4-week streak bonus", p: "+80 pts", i: "ri-fire-line" },
                ].map((item) => (
                  <div key={item.a} className="flex items-center justify-between bg-white rounded-xl px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center"><i className={`${item.i} text-emerald-600 text-sm`}></i></div>
                      <span className="text-gray-600 text-xs">{item.a}</span>
                    </div>
                    <span className="text-emerald-700 font-bold text-xs">{item.p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Plant Shop */}
        {activeTab === "shop" && (
          <div>
            {selectedPlot !== null && <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-700 text-sm font-medium">Plot #{selectedPlot + 1} selected — choose a vegetable to plant!</div>}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {VEG_CONFIG.map((veg) => {
                const canAfford = score >= veg.scoreNeeded;
                return (
                  <div key={veg.type} className={`rounded-2xl p-5 border-2 transition-all ${veg.color} ${!canAfford ? "opacity-60" : "hover:scale-105"}`}>
                    <div className="text-4xl text-center mb-3">{veg.emoji}</div>
                    <p className="font-bold text-gray-800 text-sm text-center mb-1">{veg.name}</p>
                    <div className="flex justify-center mb-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${veg.tagColor}`}>{veg.scoreNeeded} pts</span>
                    </div>
                    <div className="flex justify-center gap-1 mb-4">
                      {veg.stages.map((s, i) => <span key={i} className="text-lg">{s}</span>)}
                    </div>
                    <button
                      onClick={() => {
                        if (selectedPlot !== null) { handlePlant(veg.type, selectedPlot); }
                        else { setPlantingVeg(veg.type); showToast("Now click an empty 🌱 plot on the Farm tab!"); setActiveTab("farm"); }
                      }}
                      disabled={!canAfford}
                      className={`w-full py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${canAfford ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                    >
                      {canAfford ? (selectedPlot !== null ? "Plant Here" : "Select & Plant") : `Need ${veg.scoreNeeded - score} more pts`}
                    </button>
                  </div>
                );
              })}
            </div>
            {plantingVeg && <p className="mt-4 text-center text-sm text-emerald-600 font-medium">Go to Farm tab and click an empty 🌱 plot to plant your {VEG_CONFIG.find(v=>v.type===plantingVeg)?.name}!</p>}
          </div>
        )}

        {/* Leaderboard */}
        {activeTab === "leaderboard" && (
          <div className="max-w-lg">
            <div className="flex flex-col gap-3">
              {LEADERBOARD.map((user, i) => (
                <div key={user.name} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-4">
                  <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full font-bold text-sm ${i===0?"bg-amber-400 text-white":i===1?"bg-gray-300 text-white":i===2?"bg-orange-300 text-white":"bg-gray-100 text-gray-500"}`}>{i+1}</div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800 text-sm">{user.name}</p>
                    <p className="text-xs text-gray-400">{user.farm.join("")} · 🔥 {user.streak}wk streak</p>
                  </div>
                  <span className="text-emerald-600 font-bold text-sm">{user.score.toLocaleString()} pts</span>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-emerald-50 rounded-2xl p-5 text-center">
              <p className="text-gray-600 text-sm mb-1">Your current rank</p>
              <p className="text-3xl font-bold text-emerald-600">#12</p>
              <p className="text-gray-400 text-xs mt-1">Keep studying to climb the leaderboard!</p>
            </div>
          </div>
        )}
      </div>

      <footer className="bg-stone-900 text-white py-8 mt-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/40 text-xs">&copy; 2026 Crown SAT &amp; University · www.CrownSATandUniversity.com</p>
          <Link to="/" className="text-white/40 text-xs hover:text-white/70 transition-colors cursor-pointer">← Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}
